// Phase 2.6 — Email recon modal extracted from Partners.jsx. Drives both
// single-partner email and bulk-with-preview flows. Parent owns the state
// object + async handlers; this component is purely presentational.

import { Modal, Btn, MonthPicker } from "@/components/Shared";
import { Mail } from "lucide-react";
import { fmtPeriod } from "@/lib/api";

export default function PartnerEmailReconModal({
  emailModal, setEmailModal,
  eligibleCount,
  selectedCount, previewTotal,
  emailBusy, previewBusy,
  onFetchPreview, onSend,
  onToggleSelect, onToggleSelectAll,
}) {
  const isBulkPreview = emailModal?.target === "bulk" && emailModal?.stage === "preview";
  return (
    <Modal
      open={!!emailModal}
      onClose={() => setEmailModal(null)}
      title={
        emailModal?.target === "bulk"
          ? (emailModal?.stage === "preview" ? "Bulk email — review & confirm" : "Bulk email — pick period")
          : `Email recon · ${emailModal?.partner?.partner_name || ""}`
      }
      testid="email-modal"
      size={isBulkPreview ? "xl" : "md"}
    >
      {emailModal && emailModal.target === "one" && (
        <div className="space-y-4">
          <div className="bg-[var(--surface-2)] border border-[var(--line)] rounded-sm p-3 text-[12.5px]">
            Will email to <b className="num">{emailModal.partner?.email}</b>
          </div>
          <div>
            <label className="text-[11px] uppercase text-[var(--muted)] font-semibold tracking-wider">Period</label>
            <MonthPicker value={emailModal.period} onChange={(v) => setEmailModal({ ...emailModal, period: v })} testid="email-modal-month" />
            <div className="text-[10.5px] text-[var(--muted)] mt-1">Defaults to the previous month — change if needed.</div>
          </div>
        </div>
      )}

      {emailModal && emailModal.target === "bulk" && emailModal.stage === "pick" && (
        <div className="space-y-4">
          <div className="bg-[var(--surface-2)] border border-[var(--line)] rounded-sm p-3 text-[12.5px]">
            <b>{eligibleCount}</b> partner{eligibleCount === 1 ? "" : "s"} have an email on file.
            We&apos;ll fetch each one&apos;s net payout for the period so you can review before sending.
          </div>
          <div>
            <label className="text-[11px] uppercase text-[var(--muted)] font-semibold tracking-wider">Period</label>
            <MonthPicker value={emailModal.period} onChange={(v) => setEmailModal({ ...emailModal, period: v })} testid="bulk-period" />
            <div className="text-[10.5px] text-[var(--muted)] mt-1">Defaults to the previous month — change if needed.</div>
          </div>
        </div>
      )}

      {emailModal && isBulkPreview && (
        <div className="space-y-3">
          <div className="bg-[var(--surface-2)] border border-[var(--line)] rounded-sm p-3 text-[12.5px] flex flex-wrap items-center gap-3 justify-between">
            <div>
              Period <b className="num">{fmtPeriod(emailModal.period)}</b> · <b>{(emailModal.preview || []).length}</b> eligible partner{(emailModal.preview || []).length === 1 ? "" : "s"}.
              Rows with <span className="num">₹0</span> net are pre-deselected so you don&apos;t accidentally email a zero-payout statement.
            </div>
            <div className="flex items-center gap-2">
              <Btn variant="ghost" testid="bulk-select-all" onClick={() => onToggleSelectAll(true)}>Select all</Btn>
              <Btn variant="ghost" testid="bulk-deselect-all" onClick={() => onToggleSelectAll(false)}>Deselect all</Btn>
            </div>
          </div>
          <div className="overflow-x-auto max-h-[55vh] border border-[var(--line)] rounded-sm">
            <table className="table-data w-full" data-testid="bulk-preview-table">
              <thead><tr>
                <th style={{ width: 40 }}>
                  <input
                    type="checkbox"
                    data-testid="bulk-select-header"
                    checked={selectedCount > 0 && selectedCount === (emailModal.preview || []).length}
                    ref={(el) => { if (el) el.indeterminate = selectedCount > 0 && selectedCount < (emailModal.preview || []).length; }}
                    onChange={(e) => onToggleSelectAll(e.target.checked)}
                    className="h-4 w-4 accent-[var(--accent)]"
                  />
                </th>
                <th>Partner</th>
                <th>Email</th>
                <th className="text-right">Merchants</th>
                <th className="text-right" title="Calculation slices — splits when rates change mid-month">Lines</th>
                <th className="text-right">Gross Payout</th>
                <th className="text-right">Net Payout</th>
                <th>Status</th>
              </tr></thead>
              <tbody>
                {(emailModal.preview || []).map((r) => {
                  const checked = !!emailModal.selected[r.partner_id];
                  const zero = Number(r.net_payout || 0) <= 0;
                  return (
                    <tr key={r.partner_id} data-testid={`bulk-row-${r.partner_id}`} className={zero ? "opacity-70" : ""}>
                      <td>
                        <input
                          type="checkbox"
                          data-testid={`bulk-row-check-${r.partner_id}`}
                          checked={checked}
                          onChange={() => onToggleSelect(r.partner_id)}
                          className="h-4 w-4 accent-[var(--accent)]"
                        />
                      </td>
                      <td>
                        <div className="font-medium">{r.partner_name}</div>
                        <div className="num text-[11px] text-[var(--muted)]">{r.partner_id}</div>
                      </td>
                      <td className="num text-[12px]">{r.email}</td>
                      <td className="num text-right font-medium">{r.merchant_count ?? "—"}</td>
                      <td className="num text-right text-[var(--muted)]">{r.line_count}</td>
                      <td className="num text-right">₹ {Number(r.gross_payout || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}</td>
                      <td className={`num text-right ${zero ? "text-[var(--muted)]" : "font-semibold"}`}>
                        ₹ {Number(r.net_payout || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}
                      </td>
                      <td>
                        {zero
                          ? <span className="inline-flex items-center px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider border rounded-sm bg-amber-50 text-amber-800 border-amber-200">₹0 — skip?</span>
                          : <span className="inline-flex items-center px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider border rounded-sm bg-emerald-50 text-emerald-800 border-emerald-200">Ready</span>}
                      </td>
                    </tr>
                  );
                })}
                {!(emailModal.preview || []).length && (
                  <tr><td colSpan={8} className="text-center text-[var(--muted)] py-6 text-[12px]">No eligible partners for this period.</td></tr>
                )}
              </tbody>
            </table>
          </div>
          <div className="flex items-center justify-between text-[12px] text-[var(--muted)]">
            <span><b className="text-[var(--ink)] num">{selectedCount}</b> selected · combined net <b className="text-[var(--ink)] num">₹ {previewTotal.toLocaleString("en-IN", { maximumFractionDigits: 0 })}</b></span>
            <button
              type="button"
              onClick={() => setEmailModal({ ...emailModal, stage: "pick", preview: null, selected: {} })}
              className="text-[var(--accent)] hover:underline"
              data-testid="bulk-back"
            >← Pick a different period</button>
          </div>
        </div>
      )}

      <div className="flex flex-wrap justify-end gap-2 mt-5">
        <Btn variant="ghost" onClick={() => setEmailModal(null)} disabled={emailBusy || previewBusy}>Cancel</Btn>
        {emailModal?.target === "bulk" && emailModal.stage === "pick" && (
          <Btn variant="accent" testid="bulk-load-preview" onClick={onFetchPreview} disabled={previewBusy || !emailModal.period}>
            <Mail size={13} /> {previewBusy ? "Loading…" : "Load preview"}
          </Btn>
        )}
        {emailModal?.target === "bulk" && emailModal.stage === "preview" && (
          <Btn variant="accent" testid="bulk-send" onClick={onSend} disabled={emailBusy || selectedCount === 0}>
            <Mail size={13} /> {emailBusy ? "Sending…" : `Send to ${selectedCount}`}
          </Btn>
        )}
        {emailModal?.target === "one" && (
          <Btn variant="accent" testid="email-modal-send" onClick={onSend} disabled={emailBusy || !emailModal?.period}>
            <Mail size={13} /> {emailBusy ? "Sending…" : "Send"}
          </Btn>
        )}
      </div>
    </Modal>
  );
}
